/* eslint-disable sonarjs/cognitive-complexity */
import { useTranslation } from "next-i18next";
import { FC, useState, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRouter } from "next/router";
import Link from "@Components/Link";

import {
  getAllBadges,
  getItemQty,
  getWeightValue,
  formatNetWeight,
  getUomValue,
  getPriceOrDiscounted,
  formatUnitPriceCA,
  getQtyPerPackage,
  formatUnitPrice,
  getCTUnitPrice,
} from "@Components/cart/helpers/helpers";
import { StarRating } from "@Components/ui/star-rating/StarRating";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { IProductTileProps } from "@Types/promo-carousel/product-tile";
import { setItem } from "@Utils/browserStorage";
import { nextAppEnv } from "@Utils/environment";
import { currency } from "@Utils/index";
import { isCanadianLocale } from "@Utils/locale";
import Icon from "@wff/components/ui/icon";
import { useFavorites } from "@wff/hooks/useFavorites";
import { ICartLineItem } from "@wff/interfaces";
import { cartSliceSelector } from "@wff/store/cartSlice";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import ButtonCartAddOrUpdate from "../buttons/button-cart-add-or-update/ButtonCartAddOrUpdate";
import { ProductLink } from "./ProductLink";
import Badge from "./badge/Badge";
import ImageSlider from "./slider/ImageSlider";
import TinyPopper from "./tiny-popper/TinyPopper";
import classes from "./productTile.module.scss";

/**
 * Component that displays a product/Item's card/tile
 * @param {Props} props - Component's props
 * @returns
 */

const ProductTile: FC<IProductTileProps> = ({
  className,
  item,
  reorder = false,
  isFavorite = false,
  favoritesList,
  isFavoritePage = false,
  isPLP,
  isSRP,
  isCarousel = false,
}) => {
  const { lineItems } = useAppSelector(cartSliceSelector);
  const { ctData, tooltipData, images, src } = item || {};
  const currentProduct = item?.ctData?.masterData?.current;
  const [primaryImage, secondaryImage] = images ?? [];
  const isFixedPrice =
    ctData?.productType?.obj?.name === "Fixed Price Item" ? true : false;
  const SKU = ctData?.masterData?.current?.masterVariant?.sku ?? "";
  const itemIndex = lineItems?.findIndex(
    (item: ICartLineItem) => item.sku === SKU
  );
  const [itemQuantity, setItemQuantity] = useState(
    getItemQty(lineItems, itemIndex)
  );
  const [qtyInputValue, setQtyInputValue] = useState(
    getItemQty(lineItems, itemIndex)
  );
  const dataCurrent = ctData?.masterData?.current;
  const availability =
    dataCurrent?.masterVariant?.availability?.availableQuantity;
  const currentLocale = useAppSelector(localeSelector)?.languageCode;
  const isCanadaUser = isCanadianLocale(currentLocale);
  const lang = currentLocale;
  const slug = dataCurrent?.slug ? dataCurrent?.slug[lang] : "";
  const attribs = dataCurrent?.masterVariant?.attributes;
  const priceData = dataCurrent?.masterVariant?.prices;
  const channels = dataCurrent?.masterVariant?.availability?.channels;
  const badges = getAllBadges(attribs, lang);
  const qtyPerPackage = getQtyPerPackage(attribs);
  const weight = getWeightValue(attribs);
  const UOM = getUomValue(attribs);
  const ctUnitPrice = getCTUnitPrice(attribs);

  const checkProductIsOnStock = () => {
    const isProductAvailable =
      channels &&
      Object.keys(channels).find((key) => {
        return channels[key]?.isOnStock === true;
      });
    return isProductAvailable
      ? true
      : dataCurrent?.masterVariant?.availability?.isOnStock;
  };
  const isOnStock = checkProductIsOnStock();
  const title = dataCurrent?.name[lang];
  const netWeight = formatNetWeight(weight, UOM, currentLocale, isFixedPrice);
  const unitPrice = formatUnitPrice(ctUnitPrice, UOM);
  const unitPriceCA = formatUnitPriceCA(priceData, weight, UOM, currentLocale);
  //const totalPrice = getTotalPrice(priceData, weight, currentLocale);
  const totalPrice = currency.format(getPriceOrDiscounted(priceData));
  // const newItem = false; // TODO: Integrate this tag/label if we get a ticket for it
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const router = useRouter();
  const isSearchPLP = router?.pathname?.includes("search");
  const { handleAddFavorite, handleRemoveFavorite } = useFavorites();
  const [favIcon, setFavIcon] = useState("heart-outlined");
  const [isFavoriteProduct, setIsFavoriteProduct] = useState(false);
  const isMobile = useMediaQuery("(max-width:48em)");
  const isDisplayTwoInRow = isMobile && (isPLP || isSRP);
  const showTwoInRowCarousel = isMobile && isCarousel;

  const Badges = ({ badges }: { badges: string[] }) => (
    <>
      <Link href={`/${lang}/products/${slug}`}>
        <div className={classes["badge-container"]}>
          {badges?.map((badge, i) => {
            return i < 2 ? (
              <Badge type={"badge"} key={`badge-${i}`}>
                {badge}
              </Badge>
            ) : (
              <></>
            );
          })}
        </div>
      </Link>
      {!isOnStock && (
        <Link href={`/${lang}/products/${slug}`}>
          <div className={classes["badge-container--out-of-stock"]}>
            <Badge type={"badge--out-of-stock"} key={`badge-999`}>
              {t("products.badges.coming_soon")}
            </Badge>
          </div>
        </Link>
      )}
    </>
  );
  const incrementalPayload = {
    itemIndex: itemIndex,
    itemQuantity: itemQuantity,
    availability: availability,
    sku: SKU,
    callback: setItemQuantity,
    dispatch: dispatch,
  };

  const decrementalPayload = {
    itemIndex: itemIndex,
    itemQuantity: itemQuantity,
    lineItems: lineItems,
    sku: SKU,
    callback: setItemQuantity,
    dispatch: dispatch,
  };

  const handleOnChangeQuantity = (value: any) => {
    if (isNaN(Number(value))) {
      setQtyInputValue(qtyInputValue);
      return;
    }
    setQtyInputValue(Number(value));
  };

  const handleUpdateQuantityPayload = {
    itemQuantity: qtyInputValue,
    prevQuantity: itemQuantity,
    availability: availability,
    lineItems: lineItems,
    sku: SKU,
    callback: setQtyInputValue,
    dispatch: dispatch,
  };

  const ButtonCartAddOrUpdateProps = {
    decrementalPayload: decrementalPayload,
    handleOnChangeQuantity: handleOnChangeQuantity,
    handleUpdateQuantityPayload: handleUpdateQuantityPayload,
    incrementalPayload: incrementalPayload,
    isOnStock: isOnStock,
    itemQuantity: itemQuantity,
    qtyInputValue: qtyInputValue,
    currentProduct: currentProduct,
  };

  useEffect(() => {
    setItemQuantity(getItemQty(lineItems, itemIndex)); // TODO from Jared and Jose: could be made into a custom hook for duplicate logic in ProductDetails.tsx
    setQtyInputValue(getItemQty(lineItems, itemIndex));
  }, [lineItems, itemIndex]);

  const renderImages = () => {
    if (primaryImage) {
      return (
        <ImageSlider
          lang={lang}
          slug={slug}
          title={title}
          isOnStock={isOnStock}
          primaryImage={primaryImage}
          secondaryImage={secondaryImage}
        />
      );
    }

    if (src) {
      return (
        <ImageSlider
          lang={lang}
          slug={slug}
          title={title}
          primaryImage={src}
          isOnStock={isOnStock}
        />
      );
    }

    return (
      <ProductLink
        title={title}
        to={`/products/${slug}`}
        className={classes["product-tile__link"]}
      >
        <div style={{ width: 317, height: 317 }} />
      </ProductLink>
    );
  };
  /* below method will save vartically top position only for search PLP page  */
  const getPositionFromTop = () => {
    if (isCanadaUser && isSearchPLP) {
      setItem(
        STORAGE_KEYS.SCROLL_POSITION_PLP_CA,
        JSON.stringify(window.pageYOffset),
        STORAGE_TYPES.SESSION
      );
    }
  };

  const handleFavorite = () => {
    handleRemoveFavorite(SKU, () => false);
  };

  const isFavoritesEnableForCA =
    nextAppEnv.feature_toggle.CA.favorites === "true" ? true : false;

  const [isFavoriteState, setIsFavoriteState] = useState(false);

  useEffect(() => {
    const hasFavoriteProduct = favoritesList?.length
      ? favoritesList.some(
          (item: any) =>
            item?.ctData?.masterData?.current?.masterVariant?.sku === SKU
        )
      : false;
    favoritesList?.length
      ? setIsFavoriteState(hasFavoriteProduct)
      : setIsFavoriteState(false);
    hasFavoriteProduct
      ? setIsFavoriteProduct(true)
      : setIsFavoriteProduct(false);
  }, [favoritesList, setIsFavoriteState, SKU]);

  const handleFavoritePLP = () => {
    favIcon === "heart-outlined"
      ? setFavIcon("heart-filled")
      : setFavIcon("heart-outlined");
    if (isFavoriteProduct) {
      handleRemoveFavorite(SKU, setIsFavoriteProduct);
    } else {
      handleAddFavorite(SKU, setIsFavoriteProduct);
    }
    setIsFavoriteProduct(!isFavoriteProduct);
  };

  useEffect(() => {
    isFavoriteState ? setFavIcon("heart-filled") : setFavIcon("heart-outlined");
  }, [isFavoriteState]);

  return reorder ? (
    <ButtonCartAddOrUpdate {...ButtonCartAddOrUpdateProps} />
  ) : (
    <div
      onClick={getPositionFromTop}
      data-product-sku={SKU}
      className={`
      ${className}
      ${
        classes["product-tile"] +
        " " +
        classes["product-tile--featured"] +
        " " +
        (isOnStock ? "" : classes["availability--out-of-stock"]) +
        " " +
        classes["price--regular"] +
        " " +
        classes["price--same"] +
        " " +
        classes["product-tile--gallery"] +
        " " +
        classes["tns-item"] +
        " " +
        classes["tns-slide-active"] +
        " " +
        (isDisplayTwoInRow ? classes["product-tile--two-in-row"] : "") +
        " " +
        classes["tns-slide-active"]
      }
      ${showTwoInRowCarousel && classes["product--carousel--one-half-in-row"]}
      `}
    >
      <div className={classes["product-tile__gallery"]}>
        <div className={classes["product-tile__link"]}>
          {renderImages()}
          <Badges badges={badges} />
        </div>
      </div>
      <div className={classes["product-tile__wrapper"]}>
        <div>
          <ProductLink
            to={`/products/${slug}`}
            title={title}
            className={classes["product-tile__link"]}
          >
            <h2 className={classes["product-tile__title"]}>
              {title ? title : ""}
            </h2>
          </ProductLink>
          <div className={classes["product-tile__star-rating"]}>
            <StarRating size=".875rem" color="#FEC600" productId={SKU} />
          </div>
        </div>
        <div className={classes["product-tile__content"]}>
          <div className={classes["product-tile__footer"]}>
            <div className={classes["product-tile__packaging"]}>
              <span className={classes["product-pieces"]}>
                <span className={classes["product-pieces__label"]}>
                  {UOM === "KG" || UOM === "LB"
                    ? `${qtyPerPackage ? qtyPerPackage : ""} ${netWeight}`
                    : ""}
                </span>
              </span>
            </div>
            <div className={classes["product-tile__price-per-unit"]}>
              <div
                className={
                  unitPrice
                    ? classes["price-per-unit-show"]
                    : classes["price-per-unit-hide"]
                }
              >
                <span>{unitPrice}</span>
                <span>&nbsp;{isCanadaUser ? "|" : ""}&nbsp;</span>
                <span>{isCanadaUser ? unitPriceCA : ""}</span>
              </div>

              {isFavoritesEnableForCA && isFavorite && (
                <div className={classes["product-tile__favorite"]}>
                  {isFavoritePage && (
                    <Icon iconName={"heart-filled"} onClick={handleFavorite} />
                  )}
                  {!isFavoritePage && (
                    <Icon iconName={favIcon} onClick={handleFavoritePLP} />
                  )}
                </div>
              )}
            </div>
            <div className={classes["product-tile__pricing"]}>
              <div className={classes["pricing"]}>
                <div className={classes["pricing__values"]}>
                  <span
                    className={classes["price"] + " " + classes["price--final"]}
                  >
                    {/* <span
                      className={
                        classes["price__label"] +
                        " " +
                        classes["price__label--regular"]
                      }
                    >
                      Regular price
                    </span> */}
                    <span className={classes["price__value"]}>
                      {totalPrice}
                    </span>
                  </span>
                </div>
              </div>
              {isFixedPrice ? null : <TinyPopper tooltipData={tooltipData} />}
            </div>
            <ButtonCartAddOrUpdate
              {...ButtonCartAddOrUpdateProps}
              isDisplayTwoInRow={isDisplayTwoInRow}
              showTwoInRowCarousel={showTwoInRowCarousel}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductTile;
